import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchCompetitionGame } from '../services/competitionApi';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AdminBetScoreModal } from '../components/admin/AdminBetScoreModal';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Users,
  Target,
  Award,
  CheckCircle,
  AlertCircle,
  Pencil
} from 'lucide-react';
import { formatIsoDateTimeDMY } from '../utils/dateLocale';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { getPredictionTier } from '../utils/scores';
import { ScoreText } from '../components/ui/ScoreText';

const tierMeta = {
  exact: { Icon: Award, color: 'text-emerald-600', ring: 'ring-emerald-200' },
  outcome: { Icon: CheckCircle, color: 'text-amber-600', ring: 'ring-amber-200' },
  wrong: { Icon: AlertCircle, color: 'text-red-600', ring: 'ring-red-200' },
  pending: { Icon: Target, color: 'text-zinc-500', ring: 'ring-zinc-200' }
};

const UserBets = () => {
  const { gameId, competitionSlug } = useParams();
  const navigate = useNavigate();
  const basePath = `/c/${competitionSlug}`;
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [userBets, setUserBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBet, setEditingBet] = useState(null);

  useEffect(() => {
    if (gameId && competitionSlug) fetchGameAndBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, competitionSlug]);

  const fetchGameAndBets = async () => {
    try {
      const gameData = await fetchCompetitionGame(competitionSlug, gameId);
      setGame(gameData);

      let userBetsData = [];
      try {
        const response = await axios.get(`/bets/game/${gameId}/users`);
        const betsData = response.data.bets || response.data || [];
        userBetsData = betsData.map((bet) => ({
          ...bet,
          user: {
            _id: bet.userId,
            fullName: bet.username || bet.user?.fullName || 'Unknown User',
            username: bet.username || bet.user?.username
          },
          game: response.data.game || gameData
        }));
      } catch {
        try {
          const response = await axios.get(`/bets/game/${gameId}`);
          userBetsData = response.data;
        } catch {
          userBetsData = [];
        }
      }

      setUserBets(userBetsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'en' ? 'Failed to load data' : 'נכשל בטעינת הנתונים');
      navigate(`${basePath}/upcoming-games`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoading message={language === 'en' ? 'Loading bets…' : 'טוען הימורים…'} />
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('userBets')}
        subtitle={`${userBets.length} ${language === 'en' ? 'predictions' : 'ניבויים'}`}
        backTo={`${basePath}/upcoming-games`}
        icon={Users}
      />

      {game ? (
        <Card className="mb-6 text-center" padding="p-5">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <img
                src={game.homeGroupId?.logoUrl || '/default-team.png'}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
              <span className="font-semibold text-zinc-900">
                {getTeamDisplayName(game.homeGroupId, language)}
              </span>
            </div>
            <span className="text-zinc-400">{t('vs')}</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-zinc-900">
                {getTeamDisplayName(game.awayGroupId, language)}
              </span>
              <img
                src={game.awayGroupId?.logoUrl || '/default-team.png'}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
            </div>
          </div>
          {game.isFinished && game.finalResult ? (
            <p className="mt-3 inline-flex rounded-lg bg-pitch-600 px-3 py-1 text-sm font-semibold text-white">
              {language === 'en' ? 'Final:' : 'סופי:'}{' '}
              <ScoreText score={game.finalResult} className="ms-1" />
            </p>
          ) : null}
          {game.round ? (
            <span className="mt-2 inline-block rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-800">
              {game.round}
            </span>
          ) : null}
        </Card>
      ) : null}

      {userBets.length === 0 ? (
        <Card className="py-12 text-center">
          <Users className="mx-auto mb-3 h-12 w-12 text-zinc-300" />
          <p className="font-medium text-zinc-700">{t('noBetsPlaced')}</p>
          <p className="mt-1 text-sm text-zinc-500">
            {language === 'en'
              ? 'No predictions have been made for this game yet.'
              : 'עדיין לא בוצעו ניבויים למשחק זה.'}
          </p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {userBets.map((bet) => {
            const finished = bet.game?.isFinished && bet.game?.finalResult;
            const tier = finished
              ? getPredictionTier(bet.predictedResult, bet.game.finalResult)
              : 'pending';
            const points =
              finished && bet.isCalculated
                ? bet.points
                : finished
                  ? tier === 'exact'
                    ? 3
                    : tier === 'outcome'
                      ? 1
                      : 0
                  : null;
            const { Icon, color, ring } = tierMeta[tier] || tierMeta.pending;
            const name = bet.user?.fullName || bet.user?.username || 'User';

            return (
              <li key={bet._id}>
                <Card className={`ring-1 ${ring}`} padding="p-4">
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-600 text-lg font-bold text-white">
                      {name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-zinc-900">{name}</p>
                      <p className="mt-0.5 text-sm text-zinc-600">
                        {language === 'en' ? 'Prediction:' : 'ניבוי:'}{' '}
                        <ScoreText
                          score={bet.predictedResult}
                          className="font-semibold text-zinc-900"
                        />
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {formatIsoDateTimeDMY(bet.createdAt)}
                      </p>
                    </div>
                    {finished ? (
                      <div className={`flex shrink-0 flex-col items-center gap-1 ${color}`}>
                        <Icon className="h-6 w-6" />
                        <span className="text-sm font-bold">
                          {points} {t('points')}
                        </span>
                      </div>
                    ) : null}
                    {user?.isAdmin ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingBet(bet)}
                      >
                        <Pencil className="h-4 w-4" />
                        {language === 'en' ? 'Edit' : 'ערוך'}
                      </Button>
                    ) : null}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 flex justify-center">
        <Button variant="secondary" onClick={() => navigate(`${basePath}/upcoming-games`)}>
          {language === 'en' ? 'Back to games' : 'חזור למשחקים'}
        </Button>
      </div>

      {editingBet && game ? (
        <AdminBetScoreModal
          userId={editingBet.userId || editingBet.user?._id}
          userName={editingBet.user?.fullName || editingBet.user?.username || editingBet.username}
          game={game}
          existingBet={editingBet}
          onClose={() => setEditingBet(null)}
          onSaved={() => {
            setEditingBet(null);
            fetchGameAndBets();
          }}
        />
      ) : null}
    </PageContainer>
  );
};

export default UserBets;
